import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Icon } from '@/lib/icons';
import { themeImportErrorMessage } from '@/api/theme-import-history.contract';
import { useApp } from '@/hooks/use-app-context';
import { ThemeQuestionEditorForm } from './ThemeQuestionEditorForm';
import { ThemeQuestionEditorToolbar } from './ThemeQuestionEditorToolbar';
import { ThemeQuestionEditorList } from './ThemeQuestionEditorList';
import { ThemeQuestionDeactivateConfirmation } from './ThemeQuestionDeactivateConfirmation';
import { ThemeQuestionDeleteConfirmation } from './ThemeQuestionDeleteConfirmation';
import { ThemeQuestionWorkbookUpload } from './ThemeQuestionWorkbookUpload';
import { useThemeQuestionEditor } from './use-theme-question-editor';

interface Props {
  themeId: string;
  themeTitle: string;
  themeSectorName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'edit' | 'update';
}

export function ThemeQuestionEditorDialog({ themeId, themeTitle, themeSectorName, open, onOpenChange, mode }: Props) {
  const { t, user } = useApp();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const copy = t.admin.excelFiles.themes.questions;
  const editor = useThemeQuestionEditor(themeId, open);
  const { query } = editor;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] w-[calc(100%-2rem)] max-w-5xl flex-col gap-0 overflow-hidden rounded-2xl border border-border/50 bg-background p-0 shadow-2xl sm:w-full">
        <DialogHeader className="border-b border-border/50 px-6 py-5 text-left">
          <DialogTitle className="truncate pr-6">{(mode === 'update' ? copy.updateTitle : copy.editTitle).replace('{theme}', themeTitle)}</DialogTitle>
          <DialogDescription className="leading-6">{editor.status === 'active' && mode === 'update' ? copy.importDescription : copy.editDescription}</DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
          {editor.status === 'active' && mode === 'update' && <ThemeQuestionWorkbookUpload copy={copy} isImporting={editor.isImporting}
            onImport={(file) => void editor.importFile(file)} />}
          {editor.status === 'active' && <ThemeQuestionEditorForm copy={copy} value={editor.form} isAdding={editor.isAdding}
            onChange={editor.setForm} onAdd={() => void editor.add()} />}
          {editor.status === 'active' && mode === 'edit' && <ThemeQuestionWorkbookUpload copy={copy} isImporting={editor.isImporting}
            onImport={(file) => void editor.importFile(file)} />}
          <ThemeQuestionEditorToolbar copy={copy} search={editor.search} domain={editor.domain}
            status={editor.status} onStatusChange={editor.changeStatus}
            domains={editor.domains} selectedCount={editor.selectedIds.size}
            canDelete={user?.role === 'super_admin' || user?.role === 'SUPER_ADMIN'}
            isChangingStatus={editor.isDeactivating || editor.isReactivating}
            onSearchChange={editor.setSearch} onDomainChange={editor.setDomain}
            onAskDeactivate={() => editor.setIsConfirming(true)}
            onAskDelete={() => setIsDeleteOpen(true)}
            onReactivate={() => void editor.reactivateSelected()} />
          <p className="text-xs text-muted-foreground">{copy.loadedSearchHint}</p>
          {query.isPending && <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Icon name="loader2" className="h-4 w-4 animate-spin" />{copy.loading}
          </p>}
          {query.isError && <p role="alert" className="py-6 text-sm text-destructive">
            {themeImportErrorMessage(query.error, copy.errors)}{' '}
            <button type="button" onClick={() => void query.refetch()} className="font-semibold underline">{copy.retry}</button>
          </p>}
          {!query.isPending && !query.isError && editor.visible.length === 0 && (
            <p className="py-6 text-sm text-muted-foreground">{editor.status === 'active' ? copy.empty : copy.inactiveEmpty}</p>
          )}
          {editor.visible.length > 0 && <ThemeQuestionEditorList copy={copy} rows={editor.visible}
            themeSectorName={themeSectorName}
            selectedIds={editor.selectedIds} onToggle={editor.toggle} onToggleVisible={editor.toggleVisible} />}
        </div>
        {!query.isPending && <div className="flex items-center justify-between border-t border-border/50 px-6 py-3 text-xs">
          <span className="text-muted-foreground">{copy.count.replace('{count}', String(editor.rows.length))}</span>
          {query.hasNextPage && <button type="button" disabled={query.isFetchingNextPage}
            onClick={() => void query.fetchNextPage()} className="font-semibold disabled:opacity-40">
            {query.isFetchingNextPage ? copy.loadingMore : copy.more}
          </button>}
        </div>}
      </DialogContent>
      <ThemeQuestionDeactivateConfirmation copy={copy} count={editor.selectedIds.size}
        open={editor.isConfirming} isPending={editor.isDeactivating}
        onOpenChange={editor.setIsConfirming}
        onConfirm={() => void editor.deactivateSelected()} />
      <ThemeQuestionDeleteConfirmation copy={copy} count={editor.selectedIds.size}
        open={isDeleteOpen} isPending={editor.isDeleting}
        onOpenChange={setIsDeleteOpen} onConfirm={editor.deleteSelected} />
    </Dialog>
  );
}
